"use client";

import * as React from "react";
import Link from "next/link";
import { Cookie, Settings2, X } from "lucide-react";

import { Button } from "@/components/ui/button";

const STORAGE_KEY = "cookie_consent_v1";

type ConsentLevel = "all" | "necessary" | null;

function readConsent(): ConsentLevel {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  if (raw === "all" || raw === "necessary") return raw;
  return null;
}

function writeConsent(level: Exclude<ConsentLevel, null>) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, level);
  // Custom event żeby inne części aplikacji mogły reagować (analytics opt-in/out)
  window.dispatchEvent(
    new CustomEvent("cookie-consent-changed", { detail: { level } })
  );
}

export function CookieBanner() {
  const [visible, setVisible] = React.useState(false);
  const [showDetails, setShowDetails] = React.useState(false);

  React.useEffect(() => {
    const consent = readConsent();
    if (consent === null) {
      // Pokaż banner z opóźnieniem dla UX (po 600ms)
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  if (!visible) return null;

  const handleAccept = (level: Exclude<ConsentLevel, null>) => {
    writeConsent(level);
    setVisible(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t-2 border-primary/40 bg-background/95 shadow-2xl backdrop-blur-md">
      <div className="mx-auto max-w-6xl p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <Cookie className="h-6 w-6 shrink-0 text-primary" />
          <div className="min-w-0 flex-1 space-y-2">
            <h3 className="text-sm font-bold">🍪 Cookies i prywatność</h3>
            <p className="text-xs text-muted-foreground">
              Używamy plików cookies żeby zapewnić działanie systemu (logowanie,
              koszyk subskrypcji) oraz — za Twoją zgodą — do analityki UX i
              poprawy doświadczenia. Bez cookies analitycznych system działa
              normalnie. Szczegóły w{" "}
              <Link href="/polityka-cookies" className="font-medium text-primary hover:underline">
                Polityce cookies
              </Link>{" "}
              i{" "}
              <Link href="/polityka-prywatnosci" className="font-medium text-primary hover:underline">
                Polityce prywatności
              </Link>
              .
            </p>

            {showDetails && (
              <div className="space-y-1 rounded-md border border-border bg-muted/30 p-3 text-[11px]">
                <div className="flex items-start gap-2">
                  <input type="checkbox" checked disabled className="mt-0.5" />
                  <div>
                    <b>Niezbędne</b> (zawsze włączone) — sesja logowania, JWT, preferencje motywu, cookie consent
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <input type="checkbox" defaultChecked className="mt-0.5" id="cookie_analytics" />
                  <div>
                    <b>Analityczne</b> — pomagają zrozumieć jak korzystasz z systemu (anonymizowane dane).
                    Bez wpływu na działanie.
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" id="cookie_marketing" />
                  <div>
                    <b>Marketingowe</b> (na razie nie używamy) — w przyszłości może retargeting Facebook/Google Ads
                  </div>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => handleAccept("necessary")}
            className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground hover:bg-muted"
            aria-label="Zamknij i akceptuj tylko niezbędne"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            onClick={() => handleAccept("all")}
            className="gap-2"
          >
            Akceptuję wszystkie
          </Button>
          <Button
            onClick={() => handleAccept("necessary")}
            variant="outline"
          >
            Tylko niezbędne
          </Button>
          <Button
            onClick={() => setShowDetails(!showDetails)}
            variant="ghost"
            size="sm"
            className="gap-2"
          >
            <Settings2 className="h-3.5 w-3.5" />
            {showDetails ? "Zwiń szczegóły" : "Dostosuj"}
          </Button>
        </div>
      </div>
    </div>
  );
}
